// Função utilitária para remover acentos e normalizar string
function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^\w\s]/g, '') // Remove caracteres especiais, exceto letras/números/espacos
    .toLowerCase();
}

// Função para filtrar revistas pelo nome (article_title), ignorando acentos/case
export function filterMagazinesByName(magazines: any[], searchTerm: string, field: string = 'article_title') {
  const normalizedTerm = normalizeString(searchTerm);
  return magazines.filter((mag) => {
    const value = mag[field] || '';
    return normalizeString(value).includes(normalizedTerm);
  });
}
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { useAPICallAsMutation } from '@/service/hooks';
import { IBook, PaginatedResponse, Params } from '@/pages/Bookstore/types';
import {
  getBookActivities,
  getBookCategories,
  getBooks,
  getFavoritedBooks,
  getMyBooks,
  getExchangeableBooks,
  getReceivedBooks,
  getMyBooksDetail,
  getReadingBooks,
  getReceivedBooksMonth,
  getBooksMonth,
  favoriteBook,
  unFavoriteBook,
  setReadingBook,
  getReceivedBooksMonthBestSellers,
  getBooksDetail,
  getMonthBestSellers,
  getReadingBooksDetail,
  getDownloadURL,
  IBookDownloadResponse,
} from './api';

export enum BookstoreHook {
  Activities = 'activities',
  Books = 'books',
  CombinedBooks = 'combined-books',
  FeaturedBooks = 'featured-books',
  CombinedFeaturedBooks = 'combined-featured-books',
  Categories = 'bookstore-categories',
  MyBooks = 'my-books',
  GetBook = 'get-book',
  GetExchangeableBooks = 'get-exchangeable-books',
  GetMyBooksDetail = 'get-my-books-detail',
  GetReadingBooksDetail = 'get-reading-books-detail',
  Received = 'received',
  ReceivedMothly = 'received-monthly',
  BestSellersMothlyReceived = 'received-monthly-best-sellers',
  BestSellersMothly = 'monthly-best-sellers',
}

export function useBookCategories(type?: string) {
  return useQuery([BookstoreHook.Categories, type], () => getBookCategories(type));
}

export function useBookActivities() {
  return useQuery([BookstoreHook.Activities], () => getBookActivities());
}

export function useBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.Books, params], () => getBooks(params));
}

function getTypedParams(params: Params = {}, type: 'book' | 'audiobook'): Record<string, string> {
  const nextParams = { ...params } as Record<string, string>;

  delete nextParams.type;
  delete nextParams.audiobook;

  return { ...nextParams, type };
}

function mergePaginatedBooksResponses(
  responses: PaginatedResponse<IBook>[]
): PaginatedResponse<IBook> {
  const [firstResponse] = responses;

  return {
    ...firstResponse,
    count: responses.reduce((total, response) => total + (response.count || 0), 0),
    next: responses.find((response) => response.next)?.next || null,
    previous: responses.find((response) => response.previous)?.previous || null,
    results: responses.flatMap((response) => response.results || []),
  };
}

async function getCombinedBooks(params: Params = {}): Promise<PaginatedResponse<IBook>> {
  const [booksResponse, audiobooksResponse] = await Promise.all([
    getBooks(getTypedParams(params, 'book')),
    getBooks(getTypedParams(params, 'audiobook')),
  ]);

  return mergePaginatedBooksResponses([booksResponse, audiobooksResponse]);
}

async function getCombinedFeaturedBooks(params: Params = {}): Promise<PaginatedResponse<IBook>> {
  const [booksResponse, audiobooksResponse] = await Promise.all([
    getBooksMonth(getTypedParams(params, 'book')),
    getBooksMonth(getTypedParams(params, 'audiobook')),
  ]);

  return mergePaginatedBooksResponses([booksResponse, audiobooksResponse]);
}

export function useCombinedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.CombinedBooks, params], () => getCombinedBooks(params));
}

export function useGetBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {

  return useMutation((params: Params) => getBooks({ ...params } as Record<string, string>), options);
}

export function useGetCombinedBooksMutation(
  options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>
) {
  return useMutation((params: Params) => getCombinedBooks(params), options);
}

export function useMyBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.MyBooks, params], () => getMyBooks(params));
}

export function useExchangeableBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getExchangeableBooks(params as Record<string, string>), options);
}

export function useGetMyBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useMutation((params: Params) => getMyBooks(params as Record<string, string>), options);
}

export function useMyReadingBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useAPICallAsMutation(getReadingBooks, options as any);
}

export function useGetAudioBooks(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {

  return useMutation((params: Params) => getBooks({ ...params, audiobook: 'true' } as Record<string, string>), options);
}

export function useGetFavoritedBooksMutation(options: UseMutationOptions<PaginatedResponse<IBook>, unknown, Params>) {
  return useAPICallAsMutation(getFavoritedBooks, options);
}

export function useReceivedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.Received, params], () => getReceivedBooks(params));
}

export function useReceivedMothly(params?: Record<string, string>) {
  return useQuery([BookstoreHook.ReceivedMothly, params], () => getReceivedBooksMonth(params));
}

export function useMothlyBestSellers(params?: Record<string, string>) {
  return useQuery([BookstoreHook.BestSellersMothly, params], () => getMonthBestSellers(params));
}

export function useReceivedMothlyBestSellers(params?: Record<string, string | boolean>) {
  const stringParams = params
    ? Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)]))
    : undefined;
  return useQuery([BookstoreHook.BestSellersMothlyReceived, stringParams], () => getReceivedBooksMonthBestSellers(stringParams));
}

export function useGetBookDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetMyBooksDetail, bookId], () => getBooksDetail(bookId), options);
}

export function useGetReadingBooksDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetReadingBooksDetail, bookId], () => getReadingBooksDetail(bookId), options);
}


export function useGetMyBooksDetail(bookId: string, options?: any) {
  return useQuery([BookstoreHook.GetMyBooksDetail, bookId], () => getMyBooksDetail(bookId), options);
}

export function useFeaturedBooks(params?: Record<string, string>) {

  return useQuery([BookstoreHook.FeaturedBooks, params], () => getBooksMonth(params));
}

export function useCombinedFeaturedBooks(params?: Record<string, string>) {
  return useQuery([BookstoreHook.CombinedFeaturedBooks, params], () => getCombinedFeaturedBooks(params));
}

export function useUnFavoriteBookMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => unFavoriteBook(bookId).then((response) => response.data as IBook), options);
}

export function useFavoriteBookMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => favoriteBook(bookId).then((response) => response.data as IBook), options);
}

export function useDownloadMutation(
  options: UseMutationOptions<IBookDownloadResponse, unknown, { bookId: number; productId: number }>
) {
  return useMutation(({ bookId, productId }: { bookId: number; productId: number }) => getDownloadURL(bookId, productId), options);
}

export function useGetBookReadingMutation(options: UseMutationOptions<IBook, unknown, number>) {
  return useMutation((bookId: number) => setReadingBook(bookId), options);
}
